import { type ChangeEvent, type FC, type ReactNode, useMemo } from "react";
import { type LinearCstic, type LinearSubstep } from "~/features/configurator/Linear/models";
import { useDispatch, useSelector } from "react-redux";
import SubstepFrame from "~/features/configurator/Linear/components/SubstepFrame";
import { defaultMemoize } from "reselect";
import { Col, Row } from "~/common/components/Layout";
import {
  getCsticMap,
  getIsCustomSegmentLengthSelected,
  selectMainKmatCstics,
  selectPieceLimits,
} from "~/features/configurator/Linear/redux/selectors";
import {
  CSTIC_AL_NAME,
  CSTIC_AR_NAME,
  CSTIC_GUIDEWAY_AMOUNT_NAME,
  CSTIC_GUIDEWAY_LOCATING_FACE,
  CSTIC_LENGTH_NAME,
  CSTIC_PART_AMOUNT_NAME,
  CSTIC_PART_SIZING_TYPE_MANUAL,
  CSTIC_PART_SIZING_TYPE_NAME,
  CSTIC_PART_SIZING_TYPE_NONE,
  GET_GUIDEWAY_SET_IMAGE_URL,
  GET_RAIL_LOCATING_CSTIC_NAME,
} from "~/features/configurator/Linear/constants";
import CustomNumberFormat, { type NumberFormatEvent } from "~/common/components/CustomNumberFormat";
import {
  flushPendingInputChanges,
  resetGuidewaySet,
  updateALAR,
  updateCsticValues,
  updateGuidewayLen,
  updateGuidewayPartType,
} from "~/features/configurator/Linear/redux/actions";
import { type CustomDispatch } from "~/app/store/types";
import { type RootState } from "~/app/store";
import { useMinMaxLengthLabel } from "~/features/configurator/Linear/hooks/useMinMaxLengthLabel";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { getCsticNumberVal } from "~/features/configurator/Linear/utils";
import { PieceLengthInput } from "~/features/configurator/Linear/components/Substeps/GuidewaySetStep/components/PieceLengthInput";
import LinearReset from "~/features/configurator/Linear/components/Substeps/LinearReset";
import classNames from "classnames";
import { LinearSectionTitle } from "~/features/configurator/Linear/components/LinearSectionTitle";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { LCTileImage } from "~/features/configurator/Linear/components/LinearTile";
import { Card } from "~/features/configurator/Linear/components/Card";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { makeMinMaxLabel, useALARLogic } from "~/features/configurator/Linear/hooks/useALARLogic";
import { type DropdownEntry } from "~/common/types";
import { useTranslation } from "react-i18next";
import { getExtraValidationParamName } from "~/features/configurator/Linear/namingUtils";
import { Alert } from "~/common/mui/Alert/Alert";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { KmatType } from "~/features/configurator/Linear/kmatType";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const GuidewaySetStep: FC<Props> = ({ def, ...props }) => {
  const cstics = useSelector(selectMainKmatCstics);

  const guidewayCstic = cstics[CSTIC_GUIDEWAY_AMOUNT_NAME];
  if (!guidewayCstic) {
    return null;
  }

  const gAmount = +guidewayCstic.value!;

  const tiles = getTileData(gAmount, def, cstics);
  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      <div className={"w-100 mb-30"}>
        <Row className={"d-flex"}>
          {tiles.map((tile, index) => (
            <Col size={4} key={index}>
              <SingleGuidewayTile {...tile} />
            </Col>
          ))}
        </Row>
      </div>
    </SubstepFrame>
  );
};
export default GuidewaySetStep;

interface TileProps {
  index: number;
  def: LinearSubstep;
  railCstic: LinearCstic | undefined;
  guidewayAmount: number;
}

const SingleGuidewayTile: FC<TileProps> = (props) => {
  const { index, def, guidewayAmount } = props;

  const cstics = useSelector(getCsticMap(KmatType.SubRail, index));

  const { t } = useTranslation();
  const xmlT = useXmlConfiguratorTranslations().t!;

  // In this file we don't do the dirty length logic, because there's a value from the start courtesy of step 3
  const lengthCstic = cstics[CSTIC_LENGTH_NAME];
  const lengthHelperLabel = useMinMaxLengthLabel();
  let isLengthValid = def.additionalValidations[
    getExtraValidationParamName({
      index,
      key: CSTIC_LENGTH_NAME,
      type: KmatType.SubRail,
    })
  ] as boolean | undefined;
  if (isLengthValid === undefined) {
    isLengthValid = true;
  }

  const { aL, aR } = useALARLogic({
    def,
    cstics,
    t,
    kmatIndex: index,
  });

  const dispatch = useDispatch();

  const customSegmentLengthSelected = useSelector(getIsCustomSegmentLengthSelected(index));

  const { updateAl, updateAr, updateLength, resetToDefault } = useMemo(
    () => ({
      updateAl: (e: NumberFormatEvent) => dispatch(updateALAR(index, CSTIC_AL_NAME, SubstepType.GuidewaySet, e.value)),
      updateAr: (e: NumberFormatEvent) => dispatch(updateALAR(index, CSTIC_AR_NAME, SubstepType.GuidewaySet, e.value)),
      updateLength: (e: NumberFormatEvent) => dispatch(updateGuidewayLen(index, SubstepType.GuidewaySet, e.floatValue)),
      resetToDefault: () => {
        dispatch(resetGuidewaySet(index, SubstepType.GuidewaySet));
      },
    }),
    [dispatch, index],
  );

  const pieceLimits = useSelector(selectPieceLimits(index));

  const partAmountCstic = cstics[CSTIC_PART_AMOUNT_NAME];
  const partAmountDDValues = makePartDropdownElements(partAmountCstic, pieceLimits, index, dispatch);
  const partAmountSelectedEl = partAmountDDValues.filter((el) => el.value === partAmountCstic.value)[0];

  const partSizingCstic = cstics[CSTIC_PART_SIZING_TYPE_NAME];
  const partTypeDDValues = makePartTypeDropdownElements(partSizingCstic, index, dispatch);
  const partTypeSelectedEl = customSegmentLengthSelected
    ? partTypeDDValues.filter((el) => el.value === partSizingCstic.value)[0]
    : undefined;

  const customSizing = partTypeSelectedEl && partTypeSelectedEl.value === CSTIC_PART_SIZING_TYPE_MANUAL;

  const locatingFaceCstic = cstics[CSTIC_GUIDEWAY_LOCATING_FACE];

  const title = t(`page.linear.guidewaySet.title.${locatingFaceCstic.value == "U" ? "below" : "above"}`, {
    index: index + 1,
  });

  return (
    <Card disableHover={true}>
      <LCTileImage className={"w-100 mb-3"} imageUrl={GET_GUIDEWAY_SET_IMAGE_URL(guidewayAmount, index + 1)} />

      <div className={"px-gutter d-flex flex-direction-column flex-1"}>
        <LinearSectionTitle className={"mt-0 mb-30"}>{title}</LinearSectionTitle>

        <Alert severity={"info"} className={"pb-3"}>
          <LanguageAwareExternalContent content={def.extraTexts["upperMessage"]} />
        </Alert>

        <LinearSection label={xmlT(def.extraTexts["lengthName"])} info={def.extraTexts["lengthInfo"]}>
          <CustomNumberFormat
            value={getCsticNumberVal(lengthCstic)}
            handleState={updateLength}
            helperText={lengthHelperLabel}
            decimalScale={1}
            error={!isLengthValid}
            onBlur={flushPendingInputChanges}
          />
        </LinearSection>

        <Alert severity={"info"} className={"pb-3"}>
          <LanguageAwareExternalContent content={def.extraTexts["lowerMessage"]} />
        </Alert>

        {aL.cstic && aR.cstic && (
          <>
            <LinearSection
              label={
                <LanguageAwareExternalContent
                  content={def.extraTexts["alarName"]}
                  className={"scha__lc__external-label"}
                />
              }
              info={def.extraTexts["alarInfo"]}
            >
              <Row>
                <Col size={6}>
                  <CustomNumberFormat
                    value={aL.value}
                    error={!aL.isValid}
                    handleState={updateAl}
                    disabled={customSizing}
                    label={
                      <LanguageAwareExternalContent content={aL.cstic.label} className={"scha__lc__external-label"} />
                    }
                    decimalScale={2}
                    helperText={aL.helperLabel}
                    onBlur={flushPendingInputChanges}
                  />
                </Col>

                <Col size={6}>
                  <CustomNumberFormat
                    value={aR.value}
                    error={!aR.isValid}
                    handleState={updateAr}
                    disabled={customSizing}
                    label={
                      <LanguageAwareExternalContent content={aR.cstic.label} className={"scha__lc__external-label"} />
                    }
                    decimalScale={2}
                    helperText={aR.helperLabel}
                    onBlur={flushPendingInputChanges}
                  />
                </Col>
              </Row>
              {(aL.isNotRecommended || aR.isNotRecommended) && aL.isValid && aR.isValid ? (
                <Alert severity={"info"} className={"pb-3"}>
                  <LanguageAwareExternalContent content={def.extraTexts["alarWarningMessage"]} />
                </Alert>
              ) : null}
            </LinearSection>

            <LinearSection
              label={
                <LanguageAwareExternalContent
                  content={def.extraTexts["partsSectionName"]}
                  className={"scha__lc__external-label"}
                />
              }
              info={def.extraTexts["partsSectionInfo"]}
            >
              <Row>
                <Col size={6}>
                  <SimpleSelect
                    label={
                      <LanguageAwareExternalContent
                        content={partSizingCstic.label}
                        className={"scha__lc__external-label"}
                      />
                    }
                    values={partTypeDDValues}
                    onChange={DROPDOWN_ON_CLICK(partTypeDDValues)}
                    value={partTypeSelectedEl ? partTypeSelectedEl.value : ""}
                    fullWidth={true}
                  />
                </Col>

                <Col size={6}>
                  <SimpleSelect
                    label={t("linear.step.guideway.parts")}
                    values={partAmountDDValues}
                    onChange={DROPDOWN_ON_CLICK(partAmountDDValues)}
                    disabled={!customSegmentLengthSelected}
                    value={partAmountSelectedEl ? partAmountSelectedEl.value : ""}
                    fullWidth={true}
                  />
                </Col>
              </Row>

              {customSizing &&
                partAmountSelectedEl &&
                +partAmountSelectedEl.value > 1 &&
                new Array(+partAmountSelectedEl.value).fill(1).map((_, pieceIndex) => {
                  const isLast = pieceIndex + 1 === +partAmountSelectedEl.value;
                  const isSecondLast = pieceIndex + 2 === +partAmountSelectedEl.value;
                  return (
                    <PieceLengthInput
                      cstics={cstics}
                      kmatIndex={index}
                      pieceIndex={pieceIndex}
                      disabled={isLast}
                      isSecondLast={isSecondLast}
                      substep={SubstepType.GuidewaySet}
                      makeMinMaxLabelFormatter={makeMinMaxLabel}
                    />
                  );
                })}
            </LinearSection>
          </>
        )}

        <div className={"mt-auto mb-3"}>
          <LinearReset
            label={t("page.linear.stepReset.label.4")}
            onReset={resetToDefault}
            showConfirmation={true}
            popupMessage={t("page.linear.stepReset.confirmation")}
          />
        </div>
      </div>
    </Card>
  );
};

export const LinearSection: FC<{ label: ReactNode; info?: string; className?: string; children?: ReactNode }> = ({
  children,
  label,
  info,
  className,
}) => {
  return (
    <div className={classNames("pb-30", className)}>
      <LinearSectionTitle className={"mt-0 mb-2"} info={info}>
        {typeof label === "string" ? (
          <LanguageAwareExternalContent content={label} className={"scha__lc__external-label"} />
        ) : (
          label
        )}
      </LinearSectionTitle>
      {children}
    </div>
  );
};

const getTileData = defaultMemoize((stepAmount: number, def: LinearSubstep, cstics: Dict<LinearCstic>): TileProps[] => {
  const tileDataList: TileProps[] = [];
  for (let i = 0; i < stepAmount; i++) {
    const railCstic = cstics[GET_RAIL_LOCATING_CSTIC_NAME(i + 1)];

    tileDataList.push({
      railCstic,
      index: i,
      def,
      guidewayAmount: stepAmount,
    });
  }

  return tileDataList;
});

type EnhancedDropdownEntry = DropdownEntry<string> & { onClick: () => void };

const makePartDropdownElements = defaultMemoize(
  (
    cstic: LinearCstic | undefined,
    pieceLimits: undefined | { min: number; max: number },
    kmatIndex: number,
    dispatch: CustomDispatch<RootState>,
  ): EnhancedDropdownEntry[] => {
    if (!cstic) {
      return [];
    }
    const values = cstic.values.map((csticVal) => ({
      label: <LanguageAwareExternalContent content={csticVal.info} className={"scha__lc__external-label"} />,
      value: csticVal.value,
      onClick: () =>
        dispatch(
          updateCsticValues(
            [
              {
                type: KmatType.SubRail,
                value: csticVal.value,
                key: cstic.key,
                index: kmatIndex,
              },
            ],
            SubstepType.GuidewaySet,
          ),
        ),
    }));
    if (pieceLimits) {
      const minIndex = values.findIndex((el) => +el.value === pieceLimits.min);
      const maxIndex = values.findIndex((el) => +el.value === pieceLimits.max);
      return values.slice(minIndex, maxIndex + 1);
    } else {
      return values;
    }
  },
);
const makePartTypeDropdownElements = defaultMemoize(
  (cstic: LinearCstic | undefined, kmatIndex: number, dispatch: CustomDispatch<RootState>): EnhancedDropdownEntry[] => {
    if (!cstic) {
      return [];
    }
    return cstic.values
      .filter((cstic) => cstic.value !== CSTIC_PART_SIZING_TYPE_NONE) //do not show standard
      .map((csticVal) => ({
        label: <LanguageAwareExternalContent content={csticVal.label} className={"scha__lc__external-label"} />,
        value: csticVal.value,
        onClick: () => {
          dispatch(updateGuidewayPartType(kmatIndex, csticVal.value, SubstepType.GuidewaySet));
        },
      }));
  },
);

const DROPDOWN_ON_CLICK = (allValues: EnhancedDropdownEntry[]) => (e: ChangeEvent<HTMLInputElement>) => {
  const val = e.target.value as string;
  if (val) {
    allValues.find((item) => item.value === val)?.onClick();
  }
};
